// Location heartbeat — independent of attendance/TA_DA and of any
// activity the user is doing. Runs for any field-app user with the
// READ_GEO_LOCATION workflow permission (same permission the batch-ping
// endpoint already requires), for as long as the app tab is open:
//   * Captures one GPS fix every 5 minutes and posts it to
//     POST /api/location/pings (source: "heartbeat") — so a manager can
//     always check "where is this person right now" on the Location Map,
//     whether or not that person is punched in / mid-visit / idle.
//   * Tracks browser geolocation permission state so the UI can show a
//     persistent "enable location" banner instead of silently doing
//     nothing when permission is denied/not yet granted.
//
// Deliberately NOT the same hook as useLocationTrail.js (that one is the
// dense TA_DA beat-tracking trail, gated on punch-in + adaptive interval).
// This one is the coarse "are you even reachable" heartbeat.
import { useCallback, useEffect, useRef, useState } from "react";
import { mutate as offlineMutate } from "@/offline/apiClient";

const HEARTBEAT_MS = 5 * 60 * 1000;

export function useLocationHeartbeat({ enabled }) {
  const [permission, setPermission] = useState("unknown"); // granted|denied|prompt|unavailable|unknown
  const [lastPingAt, setLastPingAt] = useState(null);
  const timerRef = useRef(null);

  const pingOnce = useCallback(() => {
    if (!navigator.geolocation) { setPermission("unavailable"); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setPermission("granted");
        // A fix worse than 1 km (e.g. a desktop browser guessing from the internet address) is not a
        // real position — sending it would draw a false trip on the map.
        if ((pos.coords.accuracy || 0) > 1000) return;
        try {
          // Through the offline outbox so a fix taken with no signal is
          // uploaded later instead of being lost.
          await offlineMutate({
            method: "POST", path: "/location/pings",
            body: { pings: [{
              ts_client: new Date().toISOString(),
              lat: pos.coords.latitude, lng: pos.coords.longitude,
              accuracy_m: Math.round(pos.coords.accuracy || 0),
              source: "heartbeat",
            }] },
            entity_type: "location_pings", offlineCapable: true,
          });
          setLastPingAt(new Date().toISOString());
        } catch { /* offline or transient — next 10-min tick retries */ }
      },
      () => setPermission("denied"),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60 * 1000 },
    );
  }, []);

  // Track permission state via the Permissions API where available (so the
  // banner can react to the user toggling it in browser settings, not just
  // to our own getCurrentPosition calls).
  useEffect(() => {
    if (!enabled) return;
    let sub;
    (async () => {
      try {
        if (!navigator.permissions) return;
        const status = await navigator.permissions.query({ name: "geolocation" });
        setPermission(status.state);
        sub = status;
        status.onchange = () => setPermission(status.state);
      } catch { /* Permissions API not supported — fall back to callback-only state */ }
    })();
    return () => { if (sub) sub.onchange = null; };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    pingOnce();
    timerRef.current = setInterval(pingOnce, HEARTBEAT_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [enabled, pingOnce]);

  return {
    blocked: enabled && (permission === "denied" || permission === "unavailable"),
    permission, lastPingAt,
    requestPermission: pingOnce,
  };
}
